import moment from 'moment';
import {
    licencesData,
    licencesProducts,
    bonusPacks,
    bonusData,
    groupPacks,
} from 'Config/authenticated/LicencesData';
import { numformatCart, planColors } from 'Config/authenticated/dictionary/Pricingv4';
import {
    persistCart,
    computeCart,
    generateQuantity,
    fetchCart,
    fetchMetadata,
    findPackWithSameGroupIn,
} from 'api/sellsyaccountmanager';
import { debounce } from 'lodash';

export default {
    namespaced: true,
    state() {
        return {
            pricingVersion: 4,
            pricingVersionRevision: null,
            previousPricingVersion: null,
            context: 'new',
            isClient: false,
            userContext: null,
            numformat: numformatCart,
            customerId: null,
            corporationId: null,
            cartId: null,
            computeLoading: false,
            source: '',
            terms: null,
            cguvValidated: false,
            cguvVersion: null,
            termsType: 'saas',
            contact: {
                name: '',
                forename: '',
                email: '',
                phone: {
                    CC: '',
                    defaultCC: '',
                    dialCode: '',
                    e164: '',
                    e164Formated: '',
                },
                billingLocale: 'FR',
            },
            periodApplianceStart: '',
            expirationDate: moment().add('30', 'days').format('YYYY-MM-DD HH:mm:ss'),
            billingSettings: {
                algolia: '',
                name: '',
                line1: '',
                line2: '',
                line3: '',
                line4: '',
                postalCode: '',
                administrative: '',
                city: '',
                country: '',
                countryCode: '',
                manualMode: false,
            },
            plan: '',
            paymentMethod: 'stripe_card',
            earlyRenewal: false,
            vatIntra: null,
            customer: {
                allowsOneLicense: false,
            },
            accountLevel: 'trial',
            products: {},
            card: {
                cardNumber: false,
                cardExpiry: false,
                cardCvc: false,
                cardName: null,
            },
            /**
             * Dictionaries
             */
            billingLocales: [],
            plans: [],
            recurrencies: [
                {
                    code: 'MONTHLY',
                    name: window.i18n.__('Mensuel'),
                },
                {
                    code: 'YEARLY',
                    name: window.i18n.__('Annuel'),
                },
            ],
            paymentMethods: [
                {
                    label: window.i18n.__('Carte bancaire'),
                    id: 'stripe_card',
                },
                {
                    label: window.i18n.__('Virement'),
                    id: 'manual',
                },
            ],
            billingContactPayer: false,
            billingContactPayerEmail: '',
            availablesProducts: [],
            taxRate: null,
            total: {
                monthly: {
                    total_amount: null,
                    total_amount_discount: null,
                    total_amount_discounted: null,
                    total_tax_included: null,
                    total_amount_vat: null,
                },
                yearly: {
                    total_amount: null,
                    total_amount_discount: null,
                    total_amount_discounted: null,
                    total_tax_included: null,
                    total_amount_vat: null,
                },
            },
            permissions: [],
            mailCustomization: {
                messageUsed: false,
                message:
                    "<p>Madame / Monsieur,</p><p>\
                        J'ai le plaisir de vous adresser votre offre Sellsy que nous avons définie ensemble par téléphone.</p><p>\
                        Je reste à votre disposition pour toutes questions.</p><p>\
                        Cordialement,</p>",
                contactAdded: false,
                contactName: '',
                contactMailAddress: '',
                contactPhone: '',
            },
            lastPacksPicked: [],
        };
    },
    getters: {
        numformat: (state) => {
            return state.numformat;
        },
        customerId: (state) => {
            return state.customerId;
        },
        corporationId: (state) => {
            return state.corporationId;
        },
        cartId: (state) => {
            return state.cartId;
        },
        computeLoading: (state) => {
            return state.computeLoading;
        },
        contact: (state) => {
            return state.contact;
        },
        context: (state) => {
            return state.context;
        },
        billingLocales: (state) => {
            return state.billingLocales;
        },
        billingLocale: (state) => {
            return state.billingLocales.find(
                (locale) => locale.code === state.contact.billingLocale,
            );
        },
        billingSettings: (state) => {
            return state.billingSettings;
        },
        plans: (state) => {
            return state.plans.map((plan) => {
                return {
                    ...plan,
                    color: planColors[plan.code],
                };
            });
        },
        /**
         * Main Sellsy Products
         * @param {*} state
         * @returns Product[]
         */
        availablesProducts: (state) => {
            return state.availablesProducts
                .filter((product) => Object.keys(licencesProducts).includes(product.code))
                .map((product) => {
                    return { ...product, ...licencesData[licencesProducts[product.code]] };
                });
        },
        availableProductById: (state, getters) => (id) => {
            return getters.availablesProducts.find((product) => product.id === id);
        },
        availableProductPlanById: (state, getters) => (id) => {
            return getters
                .availableProductById(id)
                ?.plans.find((plan) => plan.code === getters.plan);
        },
        /**
         * Extra modules
         * @param {*} state
         * @returns Product[]
         */
        availablesPacks: (state) => {
            return state.availablesProducts
                .filter((product) => Object.keys(bonusPacks).includes(product.code))
                .map((product) => {
                    return { ...product, ...bonusData[bonusPacks[product.code]] };
                });
        },
        /**
         * Extra modules within custom plans
         * @param {*} state
         * @returns Product[]
         */
        availablesGroupedPacks: (state) => {
            return state.availablesProducts
                .filter(
                    (product) =>
                        !!product.groups.length &&
                        product.groups.some((group) => Object.keys(groupPacks).includes(group)),
                )
                .map((product) => {
                    return {
                        ...product,
                        ...bonusData[groupPacks[product.groups[0]]],
                        otherPlans: state.availablesProducts
                            .filter((item) =>
                                item.groups.some((group) => product.groups.includes(group)),
                            )
                            .map((item) => item.code),
                    };
                });
        },
        /**
         * Get single pack for each groups
         * By default, the first one, otherwise the current pack in cart (eq. have licence quantity )
         * @param {*} state
         * @param {*} getters
         * @returns Product[]
         */
        currentGroupedPacks: (state, getters) => {
            const packs = _.uniqBy(getters.availablesGroupedPacks, 'group.0');
            packs.forEach((pack, index) => {
                const lastPick = findPackWithSameGroupIn(state.lastPacksPicked, pack);
                const cartPick = findPackWithSameGroupIn(getters.groupedPacksWithLicences, pack);

                // Is previous picked pack or already in Cart ?
                const licenced = lastPick ?? cartPick;
                if (!!licenced) packs.splice(index, 1, licenced);
            });

            return packs;
        },
        allAvailablesProducts: (state, getters) => {
            return [
                ...getters.availablesProducts,
                ...getters.availablesPacks,
                ...getters.availablesGroupedPacks,
            ];
        },
        /**
         *
         * @param {*} state
         * @param {*} getters
         * @returns Current Cart products configuration
         * Inject max price rank on custom pricing when it's missing in Cart
         */
        products: (state, getters) => {
            const products = { ...state.products };

            _.each(products, (product, id) => {
                if (
                    !!getters.availableProductPlanById(id) &&
                    product.customPricing.find((price) => price.up_to === 'inf').unit_amount !==
                        getters.availableProductPlanById(id).amountInCents
                ) {
                    const freeRank = product.customPricing.find((price) => price.unit_amount === 0);
                    product.customPricing[product.customPricing.length - 1] = {
                        ...product.customPricing[product.customPricing.length - 1],
                        up_to: product.quantity,
                        min: product.customPricing[product.customPricing.length - 1].qt,
                    };
                    product.customPricing.push({
                        up_to: 'inf',
                        unit_amount: getters.availableProductPlanById(id).amountInCents,
                        qt: 0,
                        min: !!freeRank && getters.userIsClient ? freeRank.qt : 0,
                    });
                }
            });
            return products;
        },
        productsWithLicences: (state, getters) => {
            return getters.availablesProducts.filter(
                (product) => getters.products[product.id].quantity !== 0,
            );
        },
        packsWithLicences: (state, getters) => {
            return getters.availablesPacks.filter(
                (product) => getters.products[product.id].quantity !== 0,
            );
        },
        groupedPacksWithLicences: (state, getters) => {
            return getters.availablesGroupedPacks.filter(
                (product) => getters.products[product.id].quantity !== 0,
            );
        },
        freeLicencesByProduct: (state) => (id) => {
            return state.products[id].customPricing.find((price) => price.unit_amount === 0)?.qt;
        },
        modelForm: (state) => {
            return {
                ...state.contact,
                ...state.card,
                phone: state.contact.phone.e164,
                billingSettings: state.billingSettings,
                vatIntra: state.vatIntra,
                products: state.products,
                paymentMethod: state.paymentMethod,
                billingContactPayerEmail: state.billingContactPayerEmail,
            };
        },
        periodApplianceStart: (state) => {
            return state.periodApplianceStart;
        },
        /**
         * Get entire plan label: ESSENTIAL_YEARLY
         */
        plan: (state) => {
            return state.plan;
        },
        /**
         * Get plan first part (id/ code): ESSENTIAL
         */
        currentPlan: (state) => {
            return _.first(state.plan?.split('_'));
        },
        /**
         * Get plan first part(label / name): ESSENTIALS
         */
        currentPlanLabel: (state, getters) => {
            return state.plans
                ?.find((plan) => plan.code === getters.currentPlan)
                ?.name.toUpperCase();
        },
        /**
         * Get plan last part: YEARLY
         */
        recurrency: (state) => {
            return _.last(state.plan.split('_'));
        },
        recurrencies: (state) => {
            return state.recurrencies.map((item) => {
                return { label: item.name, value: item.code };
            });
        },
        vatIntra: (state) => {
            return state.vatIntra;
        },
        customer: (state) => {
            return state.customer;
        },
        isYearly: (state, getters) => {
            return getters.recurrency === 'YEARLY';
        },
        isMonthly: (state, getters) => {
            return getters.recurrency === 'MONTHLY';
        },
        paymentMethod: (state) => {
            return state.paymentMethod;
        },
        totalYearly: (state) => {
            return state.total.yearly;
        },
        totalMonthly: (state) => {
            return state.total.monthly;
        },
        source: (state) => {
            return state.source;
        },
        taxRate: (state) => {
            return state.taxRate;
        },
        paymentMethods: (state) => {
            return state.paymentMethods;
        },
        paymentMethodLabel: (state) => {
            return state.paymentMethods.find((method) => method.id === state.paymentMethod).label;
        },
        isSourceSale: (state) => {
            return state.source === 'sales';
        },
        labelPlan: (state, getters) => {
            return getters.isYearly
                ? window.i18n.__('Abonnement annuel')
                : window.i18n.__('Abonnement mensuel');
        },
        expirationDate: (state) => {
            return state.expirationDate;
        },
        licencesOnly: (state, getters) => {
            const licenceKeys = getters.availablesProducts.map((item) => item.id);
            const filteredLicences = Object.entries(state.products).filter(([key, value]) =>
                licenceKeys.includes(key),
            );
            return Object.fromEntries(filteredLicences);
        },
        quantityFreeLicences: (state, getters) => {
            return _.flatMap(getters.licencesOnly, (product) =>
                product.customPricing
                    .filter((price) => price.unit_amount === 0)
                    .map((price) => price.qt),
            ).reduce((acc, val) => acc + val, 0);
        },
        quantityPaidLicences: (state, getters) => {
            return getters.quantityLicences - getters.quantityFreeLicences;
        },
        quantityLicences: (state, getters) => {
            return _.map(getters.licencesOnly, (product) => product.quantity).reduce(
                (acc, val) => acc + val,
                0,
            );
        },
        cguvValidated: (state) => {
            return state.cguvValidated;
        },
        cguvVersion: (state) => {
            return state.cguvVersion;
        },
        termsType: (state) => {
            return state.termsType;
        },
        terms: (state) => {
            return state.terms;
        },
        cguvAlreadyValidated: (state) => {
            return state.terms.status === 'accepted';
        },
        card: (state) => {
            return state.card;
        },
        isCardMethod: (state) => {
            return state.paymentMethod === 'stripe_card';
        },
        isManualMethod: (state) => {
            return state.paymentMethod === 'manual';
        },
        isNew: (state) => {
            return state.context === 'new';
        },
        isUpsell: (state) => {
            return state.context === 'upsale';
        },
        isRenew: (state) => {
            return state.context === 'renew';
        },
        permissions: (state) => {
            return state.permissions;
        },
        isBillingContactPayer: (state) => {
            return !!state.billingContactPayer;
        },
        billingContactPayerEmail: (state) => {
            return state.billingContactPayerEmail;
        },
        userContext: (state) => {
            return state.userContext;
        },
        /**
         * Get the state of customer sale message field
         * @param {*} state
         * @returns boolean
         */
        isCustomMessageUsed: (state) => {
            return state.mailCustomization.messageUsed;
        },

        /**
         * Get the content of the customer sale message field. default is empty.
         * @param {*} state
         * @returns string
         */
        customMessage: (state) => {
            return state.mailCustomization.message;
        },
        userIsClient: (state) => {
            return state.isClient;
        },
        isTrial: (state) => {
            return state.accountLevel === 'trial';
        },
        isGold: (state) => {
            return state.accountLevel === 'gold';
        },
        earlyRenewal: (state) => {
            return state.earlyRenewal;
        },
        previousPricingVersion: (state) => {
            return state.previousPricingVersion;
        },
    },
    mutations: {
        setUserType(state, type) {
            state.isClient = type;
        },
        setContact(state, payload) {
            state.contact = payload;
        },
        setBillingLocales(state, payload) {
            state.billingLocales = payload;
        },
        setBillingSettings(state, payload) {
            state.billingSettings = payload;
        },
        setPricingVersionRevision(state, payload) {
            state.pricingVersionRevision = payload;
        },
        setPricingVersion(state, payload) {
            state.pricingVersion = payload;
        },
        setPlans(state, payload) {
            state.plans = payload;
        },
        setAvailablesProducts(state, payload) {
            state.availablesProducts = payload;
        },
        setComputeLoading(state, payload) {
            state.computeLoading = payload;
        },
        setPeriodApplianceStart(state, payload) {
            state.periodApplianceStart = payload;
        },
        setExpirationDate(state, payload) {
            state.expirationDate = payload;
        },
        setPlan(state, payload) {
            state.plan = payload;
        },
        setVatIntra(state, payload) {
            state.vatIntra = payload;
        },
        setCustomer(state, payload) {
            state.customer = payload;
        },
        setProducts(state, payload) {
            state.products = payload;
        },
        setCustomerId(state, payload) {
            state.customerId = payload;
        },
        setCorporationId(state, payload) {
            state.corporationId = payload;
        },
        setCartId(state, payload) {
            state.cartId = payload;
        },
        setPaymentMethod(state, payload) {
            state.paymentMethod = payload;
        },
        setBillingContactPayer(state, payload) {
            state.billingContactPayer = payload;
        },
        setBillingContactPayerEmail(state, payload) {
            state.billingContactPayerEmail = payload;
        },
        setCartTotal(state, payload) {
            state.total = payload;
        },
        setSource(state, payload) {
            state.source = payload;
        },
        setTaxRate(state, payload) {
            state.taxRate = payload;
        },
        setCguvStatus(state, payload) {
            state.cguvValidated = payload;
        },
        setCguvVersion(state, payload) {
            state.cguvVersion = payload;
        },
        setTerms(state, payload) {
            state.terms = payload;
        },
        setCardNumber(state, payload) {
            state.card.cardNumber = payload;
        },
        setCardExpiry(state, payload) {
            state.card.cardExpiry = payload;
        },
        setCardCvc(state, payload) {
            state.card.cardCvc = payload;
        },
        setCardName(state, payload) {
            state.card.cardName = payload;
        },
        setPermissions(state, payload) {
            state.permissions = payload;
        },
        setUserContext(state, payload) {
            state.userContext = payload;
        },
        setCustomMessageUsed(state, payload) {
            state.mailCustomization.messageUsed = payload;
        },
        setCustomMessage(state, payload) {
            state.mailCustomization.message = payload;
        },
        setCustomContact(state, payload) {
            state.mailCustomization.contactAdded = payload;
        },
        setCustomContactName(state, payload) {
            state.mailCustomization.contactName = payload;
        },
        setCustomContactPhone(state, payload) {
            state.mailCustomization.contactPhone = payload;
        },
        setCustomContactMailAddress(state, payload) {
            state.mailCustomization.contactMailAddress = payload;
        },
        setAccountLevel(state, level) {
            state.accountLevel = level;
        },
        setPreviousPricingVersion(state, version) {
            state.previousPricingVersion = version;
        },
        setEarlyRenewal(state, isEarly) {
            state.earlyRenewal = isEarly;
            if (state.context === 'renew') {
                state.earlyRenewDate = isEarly
                    ? null
                    : moment(state.previousCart.serviceEndDate).add(1, 'day').format();
            }
        },
        addPackHistory(state, lastPick) {
            const picked = state.lastPacksPicked.findIndex((licenced) =>
                licenced.groups.some((groupLicenced) => lastPick.groups.includes(groupLicenced)),
            );
            if (picked !== -1) {
                state.lastPacksPicked.splice(picked, 1, lastPick);
            } else {
                state.lastPacksPicked.push(lastPick);
            }
        },
    },
    actions: {
        fetchMetadata({ commit, dispatch, getters }) {
            commit('setComputeLoading', true);
            fetchMetadata(getters.customerId, getters.context, getters.termsType).then((res) =>
                dispatch('setMetadata', res),
            );
        },
        setMetadata({ commit, dispatch, getters }, res) {
            commit('setContact', {
                name: res.billingSettings.contactName,
                forename: res.billingSettings.contactFirstname,
                email: res.billingSettings.contactEmail,
                phone: {
                    CC: '',
                    defaultCC: '',
                    dialCode: '',
                    e164: res.billingSettings.contactPhone,
                    e164Formated: res.billingSettings.contactPhone,
                },
                billingLocale: res.billingSettings.locale
                    ? res.billingSettings.locale
                    : res.cart.billingLocale,
            });
            commit('setBillingLocales', res.billingLocales);

            commit(
                'setEarlyRenewal',
                window.sellsyapp?.config.globalProperties.$route.query.early
                    ? window.sellsyapp.config.globalProperties.$route.query.early === 'true'
                    : res.cart.earlyRenewal,
            );
            if (res.cart.earlyRenewal) {
                window.sellsyapp.config.globalProperties.$router.replace({
                    name: window.sellsyapp.config.globalProperties.$route.name,
                    query: { early: 'true' },
                });
            }
            commit('setBillingSettings', {
                ...getters.billingSettings,
                ...res.billingAddress,
                countryCode: res.billingAddress.country,
                manualMode: !!(res.billingAddress.line1 && res.billingAddress.country),
            });
            commit('setAvailablesProducts', res.products);
            commit('setPlans', res.plans);
            commit('setVatIntra', res.billingSettings.vatIdentificationNumber);
            commit('setCustomer', res.customer);
            commit('setPermissions', res.permission);
            commit('setPreviousPricingVersion', res.billingSettings.pricingVersionCustomer);

            if (_.isNil(getters.cartId)) {
                commit('setPlan', res.cart.plan);
                commit('setPaymentMethod', res.cart.paymentMethod);
                commit('setPricingVersionRevision', res.billingSettings.pricingVersionRevision);

                if (getters.context !== 'renew' && getters.context !== 'new') {
                    commit('setPricingVersion', res.billingSettings.pricingVersion);
                }
            }
            if (!_.isNil(res.terms)) {
                commit('setTerms', res.terms);
                commit('setCguvVersion', res.terms.version);
            }
            dispatch('initProductLicences').then(() => {
                if (
                    !_.isNil(res.cart.id) &&
                    window.sellsyapp.config.globalProperties.$route.params.id === 'new'
                ) {
                    commit('setCartId', res.cart.id);
                    dispatch('fetchCart', res.cart.id);
                }
            });

            commit('setComputeLoading', false);
        },
        fetchCart({ dispatch }, payload) {
            fetchCart(payload, {
                user_context: window.sellsyapp.config.globalProperties.$route.meta.user_context,
            }).then((res) => dispatch('setCart', res));
        },
        async setCart({ commit, dispatch, getters }, payload) {
            commit('setSource', payload.source);
            commit('setCartId', payload.id);
            dispatch('updatePlan', payload.plan.split('_')[0]);
            dispatch('updateRecurrency', payload.plan.split('_')[1]);
            commit('setPaymentMethod', payload.paymentMethod);
            commit('setPricingVersionRevision', payload.pricingVersionRevision);
            commit('setPricingVersion', payload.pricingVersion);
            commit('setPeriodApplianceStart', payload.activationDate);
            commit('setExpirationDate', payload.expirationDate);
            // Reset default SuiteCRM Licences when Metadata (products) are already set
            if (!!getters.availablesProducts.length) {
                await dispatch('setDefaultSuiteLicence', {
                    products: getters.products,
                    quantity: 0,
                }).then((res) => commit('setProducts', res));
            }
            // Build cart products quantity
            _.each(payload.products, (product, key) =>
                dispatch('updateProductQuantity', {
                    [key]: generateQuantity(product, getters.userIsClient),
                }),
            );
            commit('setComputeLoading', false);
        },
        initProductLicences({ commit, getters, dispatch }) {
            return new Promise(
                (resolve, reject) => {
                    let products = getters.products;
                    getters.allAvailablesProducts.forEach((product) => {
                        if (_.isNil(getters.products[product.id])) {
                            products = {
                                ...products,
                                [product.id]: {
                                    quantity: 0,
                                    customPricing: [
                                        {
                                            up_to: 'inf',
                                            unit_amount: product.plans.find(
                                                (plan) => plan.code === getters.plan,
                                            ).amountInCents,
                                            qt: 0,
                                        },
                                    ],
                                },
                            };
                        }
                    });

                    /**
                     * Set minimal 2 licences on SuiteCRM pack only on NEW cart
                     */
                    const newCartRoutes = [
                        'sam.v4.cart.form',
                        'sam.v4.client.form',
                        'sam.v4.client.form.plan',
                        'sam.v4.client.form.configuration',
                        'sam.v4.client.form.payment',
                    ];
                    if (
                        !_.isNil(window.sellsyapp) &&
                        window.sellsyapp.config.globalProperties.$route.params.id === 'new' &&
                        newCartRoutes.includes(window.sellsyapp.config.globalProperties.$route.name)
                    ) {
                        if (
                            _.map(products, (product) => product.quantity).reduce(
                                (acc, val) => acc + val,
                                0,
                            ) === 0
                        ) {
                            dispatch('setDefaultSuiteLicence', { products, quantity: 2 }).then(
                                (res) => (products = res),
                            );
                        }
                    }

                    commit('setProducts', products);
                    resolve();
                },
                (err) => {
                    reject(err);
                },
            );
        },
        setDefaultSuiteLicence({ getters }, { products, quantity }) {
            return new Promise((resolve) => {
                let items = products;
                const packSuiteId = getters.availablesProducts.find(
                    (product) => product.code === 'pack_all_included',
                ).id;
                const Suite = items[packSuiteId];
                Suite.quantity = quantity;
                Suite.customPricing.find((price) => price.up_to === 'inf').qt = quantity;
                items = {
                    ...items,
                    [packSuiteId]: Suite,
                };

                resolve(items);
            });
        },
        updateContact({ commit }, payload) {
            commit('setContact', payload);
        },
        updateRecurrency({ commit, getters, dispatch }, payload) {
            let plan = getters.plan.split('_');
            plan[1] = payload.toUpperCase();
            plan = plan.join('_');

            let products = getters.products;
            _.forEach(products, (product, id) => {
                products = {
                    ...products,
                    [id]: {
                        ...product,
                        customPricing: product.customPricing.map((pricing) => {
                            const productInfos = getters.allAvailablesProducts.find(
                                (item) => item.id === id,
                            );
                            const previousMaxAmount = productInfos.plans.find(
                                (pl) => pl.code === getters.plan,
                            ).amountInCents;
                            const nextMaxAmount = productInfos.plans.find(
                                (pl) => pl.code === plan,
                            ).amountInCents;

                            let unit_amount;

                            /**
                             * Check if pricing amount is previously equal to max allowed amount to transform on max amount on new recurrency.
                             * In case of custom pricing && exist in new recurrency, keep the monthly pricing by duplicate or divide to correspond with allowed amounts
                             */
                            if (
                                pricing.unit_amount === previousMaxAmount &&
                                pricing.unit_amount !== 0
                            ) {
                                // No customPricing
                                unit_amount = nextMaxAmount;
                            } else if (plan === getters.plan) {
                                // Plan don't change
                                unit_amount = pricing.unit_amount;
                            } else {
                                const nextAmount =
                                    payload.toUpperCase() === 'YEARLY'
                                        ? pricing.unit_amount * 12
                                        : pricing.unit_amount / 12;
                                unit_amount =
                                    productInfos.plans
                                        .find((pl) => pl.code === plan)
                                        .allowedCustomPricingAmountsInCents.includes(nextAmount) ||
                                    pricing.unit_amount === 0
                                        ? nextAmount
                                        : nextMaxAmount;
                            }

                            return {
                                ...pricing,
                                unit_amount,
                            };
                        }),
                    },
                };
            });

            // Force stripe_card payment on Monthly recurrency
            if (payload.toUpperCase() === 'MONTHLY') commit('setPaymentMethod', 'stripe_card');

            commit('setPlan', plan);
            commit('setProducts', products);
            dispatch('computeCart');
        },
        updatePlan({ commit, getters, dispatch }, payload) {
            let plan = getters.plan.split('_');
            plan[0] = payload.toUpperCase();
            plan = plan.join('_');
            let products = getters.products;
            _.forEach(products, (product, id) => {
                products = {
                    ...products,
                    [id]: {
                        ...product,
                        customPricing: product.customPricing.map((pricing) => {
                            const productInfos = getters.allAvailablesProducts.find(
                                (item) => item.id === id,
                            );
                            const previousMaxAmount = productInfos.plans.find(
                                (pl) => pl.code === getters.plan,
                            ).amountInCents;
                            const nextMaxAmount = productInfos.plans.find(
                                (pl) => pl.code === plan,
                            ).amountInCents;

                            let unit_amount;
                            /**
                             * Check if pricing amount is previously equal to max allowed amount to transform on max amount on new plan.
                             * In case of custom pricing, check if available in other plan, if not then reset to max amount
                             */
                            if (
                                pricing.unit_amount === previousMaxAmount &&
                                pricing.unit_amount !== 0
                            ) {
                                // No customPricing
                                unit_amount = nextMaxAmount;
                            } else if (plan === getters.plan) {
                                // Plan don't change
                                unit_amount = pricing.unit_amount;
                            } else {
                                const nextAmount = pricing.unit_amount;
                                unit_amount =
                                    productInfos.plans
                                        .find((pl) => pl.code === plan)
                                        .allowedCustomPricingAmountsInCents.includes(nextAmount) ||
                                    pricing.unit_amount === 0
                                        ? nextAmount
                                        : nextMaxAmount;
                            }
                            return {
                                ...pricing,
                                unit_amount,
                            };
                        }),
                    },
                };
            });
            commit('setPlan', plan);
            commit('setProducts', products);
            dispatch('computeCart');
        },
        updateProductQuantity({ commit, getters, dispatch }, payload) {
            const productId = Object.keys(payload)[0];
            const products = {
                ...getters.products,
                [productId]: {
                    ...payload[productId],
                    quantity: payload[productId].customPricing
                        .map((price) => price.qt)
                        .reduce((acc, val) => acc + val, 0),
                },
            };
            commit('setProducts', _.cloneDeep(products));
            dispatch('computeCart');
        },
        computeCart: debounce(({ state, getters, commit }) => {
            commit('setComputeLoading', true);

            if (!getters.customer.allowsOneLicense && getters.quantityPaidLicences < 2) return;
            if (getters.customer.allowsOneLicense && getters.quantityPaidLicences < 1) return;

            return new Promise((resolve, reject) => {
                SipClient.post(
                    `/customers/${getters.customerId}/compute-cart`,
                    {},
                    computeCart(state),
                )
                    .then((res) => {
                        const products = getters.products;
                        _.each(getters.products, (product, productId) => {
                            if (!res.data.products[productId]) return;

                            products[productId] = {
                                ...products[productId],
                                total: {
                                    monthly: res.data.products[productId].monthly,
                                    yearly: res.data.products[productId].yearly,
                                },
                            };
                        });
                        commit('setTaxRate', res.data.tax_rate);
                        commit('setProducts', products);
                        commit('setCartTotal', {
                            monthly: res.data.monthly,
                            yearly: res.data.yearly,
                        });
                        resolve();
                    })
                    .catch((err) => {
                        console.log(err);
                        reject();
                    })
                    .finally(() => {
                        commit('setComputeLoading', false);
                    });
            });
        }, 200),
        createCart({ state, commit }) {
            commit('setComputeLoading', true);
            return new Promise((resolve, reject) => {
                window.SellsyJS.WebServiceCaller.post(
                    `/rest/sellsy-account-manager/cart`,
                    {},
                    persistCart(state),
                    (res) => {
                        if (res.error) reject(res);
                        commit('setComputeLoading', false);
                        commit('setCartId', res.id);
                        resolve(res);
                    },
                    (err) => {
                        commit('setComputeLoading', false);
                        reject(err.response.data.error.message);
                    },
                );
            });
        },
        updateCart({ state, commit, getters }, payload) {
            commit('setComputeLoading', true);
            return new Promise((resolve, reject) => {
                window.SellsyJS.WebServiceCaller.put(
                    `/rest/sellsy-account-manager/cart/${payload}`,
                    { user_context: getters.userContext },
                    persistCart(state),
                    (res) => {
                        commit('setComputeLoading', false);
                        resolve();
                    },
                    (err) => {
                        commit('setComputeLoading', false);
                        reject(err.response.data.error.message);
                    },
                );
            });
        },
        markAsPaid({ getters }) {
            return new Promise((resolve, reject) => {
                window.SellsyJS.WebServiceCaller.post(
                    '/rest/sellsy-account-manager/cart/' + getters.cartId + '/check-payment',
                    {},
                    {},
                    (response) => {
                        if (response.error) reject(response);
                        resolve(response);
                    },
                );
            });
        },
        createPaymentIntent({ getters }) {
            return new Promise((resolve, reject) => {
                window.SellsyJS.WebServiceCaller.post(
                    '/rest/sellsy-account-manager/cart/' + getters.cartId + '/payment-intents',
                    {},
                    {},
                    (response) => {
                        if (response.error) reject(response);
                        resolve(response);
                    },
                );
            });
        },
        switchPackPlan({ getters, dispatch, commit }, nextPlan) {
            const nextPack = getters.availablesGroupedPacks.find((pack) => pack.code === nextPlan);
            /**
             * Store some last group Pack picked for prevent nextTick error on switch product in component
             */
            commit('addPackHistory', nextPack);
            dispatch('updateProductQuantity', {
                [nextPack.id]: {
                    ...getters.products[nextPack.id],
                    customPricing: [
                        {
                            ...getters.products[nextPack.id].customPricing[0],
                            qt: 1,
                        },
                    ],
                },
            });
        },
    },
};
